import { isEqual } from 'underscore';
import { logProgramChange } from '@shoflo/studio-redux/utils';

import { isDifferentDirector } from 'actions/Broadcast/utils';
import setProgram from 'actions/Broadcast/program/setProgram';
import sceneLayouts, { SINGLE } from 'constants/SceneLayouts';
import setSceneLayout from 'actions/Broadcast/program/setSceneLayout';
import { updateSourcesCameraStatus } from 'actions/Broadcast/scene';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';
import getUpdatedBannerVisibility from 'store/utils/getUpdatedBannerVisibility';
import updateBanners from '@shoflo/studio-redux/actions/banner/updateBanners';

/**
 * Builds a new program replacing current program state.
 *
 * This is meant to be called after another logged in director changes the state, emitting
 * a BROADCAST_PROGRAM_CHANGED event, which necessitates that all other directors have their
 * programs updated.
 *
 * Care should be taken that the director who emits the change is excluded from the update, as
 * that would cause an infinite loop.
 */
export default ({ program: updatedProgram, notifyingDirector }) => {
  return (dispatch, getState) => {
    const {
      program,
      banners,
      meeting: { attendeeResponse }
    } = getState();
    if (!isEqual(program, updatedProgram)) {
      const attendeeId =
        attendeeResponse && attendeeResponse.Attendee.AttendeeId;
      const vendor = shouldHaveFeature('studio.meetingVendor');
      if (!attendeeId) return;
      if (isDifferentDirector(notifyingDirector, attendeeId)) {
        // Update camera statuses for sources in the incoming program
        // This ensures cameras are turned on/off to match the program state
        if (updatedProgram.layers) {
          dispatch(
            updateSourcesCameraStatus(updatedProgram.layers, { vendor })
          );
          const updatedBanners = getUpdatedBannerVisibility(
            updatedProgram,
            banners
          );
          dispatch(updateBanners(updatedBanners));
        }

        // When program is updated by a scene the id is not a layout in that
        // scenario we want to get the layout from the program title.
        const layout = sceneLayouts.find(
          l => l.id === updatedProgram?._id || l.id === updatedProgram?.layout
        );
        dispatch(setSceneLayout(layout || SINGLE));
        /**
         * The syncProgram action is meant to synchronize local state w/
         * some other authority (e.g. the server, or another director)
         * so we don't want to emit the program change to the socket server
         * in this case. If we were to emit the program change,
         * we would run the risk of entering into an infinite loop
         * between other sources of authority. i.e Multiple directors
         */
        dispatch(setProgram(updatedProgram, true, { emitSocket: false }));
        logProgramChange(getState, 'syncProgram action');
      }
    }
  };
};
